import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { getCompany, fetchCompanyDetails } from '../../../redux/companySlice';
import { http_get, http_post } from '../../pages/utils/api_client';
import { API_ENDPOINTS, CONSTS } from '../../pages/utils/endpoints';
import LMLineTab from '../line_tab/LMLineTab';

import Toolbar from './Toolbar';
import GroupedMaterialCard from './GroupedMaterialCard';
import BulkActionBar from './BulkActionBar';
import EmptyState from './EmptyState';
import InviteModal from './InviteModal';
import ImportCsvModal from './ImportCsvModal';
import './ManageAccess.scss';

const LABELS = {
  walkthrough: {
    title: 'Walkthroughs',
    tabName: CONSTS.WALKTHROUGHS,
    unitUpper: 'STEPS',
    stepPrefix: '',
    groupByLabel: 'Walkthrough',
    resultNoun: 'walkthroughs',
  },
  paths: {
    title: 'Paths',
    tabName: CONSTS.PATHS,
    unitUpper: 'STAGES',
    stepPrefix: 'Stage ',
    groupByLabel: 'Path',
    resultNoun: 'paths',
  },
};

const ManageAccessPage = ({ materialType = 'walkthrough' }) => {
  const dispatch = useDispatch();
  const company = useSelector(getCompany);
  const labels = LABELS[materialType] || LABELS.walkthrough;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(1);
  const limit = 10;

  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ range: [], total_pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePreselectId, setInvitePreselectId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const openInvite = (materialId = null) => {
    setInvitePreselectId(materialId);
    setInviteOpen(true);
  };

  useEffect(() => {
    dispatch(fetchCompanyDetails());
  }, [dispatch]);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    http_get(API_ENDPOINTS.GET_MANAGE_ACCESS, {
      material_type: materialType,
      search: debouncedSearch,
      status_filter: statusFilter,
      sort,
      page,
      limit,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const recs = data.records || [];
        setRecords(recs);
        setPagination(data.pagination || { range: [], total_pages: 1 });
        setExpanded(new Set(recs.map((r) => r.id)));
        setSelectedIds(new Set());
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(err);
        toast.error('Something went wrong loading access data');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [materialType, debouncedSearch, statusFilter, sort, page, refreshKey]);

  const comingSoon = (label) => toast.info(`${label} — coming soon`);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleRow = (inviteId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(inviteId)) next.delete(inviteId); else next.add(inviteId);
      return next;
    });
  };

  const toggleAllInCard = (ids, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Revoke access (private materials only) — keyed by material + audience.
  const revokeAccess = async (materialId, audienceIds) => {
    try {
      const { data } = await http_post(API_ENDPOINTS.BULK_REVOKE_MATERIAL_INVITES, {
        learning_material_id: materialId,
        audience_ids: audienceIds,
      });
      if (data?.error) { toast.error(data.error); return; }
      const n = data.revoked_count ?? audienceIds.length;
      toast.success(`Revoked access for ${n} learner${n === 1 ? '' : 's'}`);
      clearSelection();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error('Could not revoke access');
    }
  };

  const handleRevokeRow = (materialId, audienceId) => {
    if (!window.confirm('Revoke this learner’s access to this content?')) return;
    revokeAccess(materialId, [audienceId]);
  };

  const handleBulkRevoke = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Revoke access for ${selectedIds.size} selected learner${selectedIds.size === 1 ? '' : 's'}? (Applies to private content only.)`)) return;
    const byMaterial = {};
    selectedIds.forEach((key) => {
      const [mid, aid] = key.split(':');
      (byMaterial[mid] ||= []).push(Number(aid));
    });
    Object.entries(byMaterial).forEach(([mid, aids]) => revokeAccess(Number(mid), aids));
  };

  const goToPage = (p) => {
    setPage(p);
    clearSelection();
  };

  const resultLabel = useMemo(() => {
    const materialsOnPage = records.length;
    const accessGrants = records.reduce((sum, m) => sum + (m.users_count || 0), 0);
    const uniqueUsers = new Set();
    records.forEach((m) => (m.rows || []).forEach((r) => uniqueUsers.add(r.audience_id)));
    return `Showing ${materialsOnPage} ${labels.resultNoun} · ${accessGrants} access grants across ${uniqueUsers.size} unique users`;
  }, [records, labels.resultNoun]);

  const isFiltering = debouncedSearch !== '' || statusFilter !== 'all';
  const showEmpty = loaded && !isLoading && records.length === 0;

  return (
    <div className="un-page-container__learning-materials ma-page">
      <ToastContainer position="bottom-right" autoClose={3500} />

      {inviteOpen && (
        <InviteModal
          materials={records}
          initialMaterialId={invitePreselectId}
          materialType={materialType}
          onClose={() => setInviteOpen(false)}
          onSent={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {importOpen && (
        <ImportCsvModal
          materials={records}
          materialType={materialType}
          onClose={() => setImportOpen(false)}
          onDone={() => setRefreshKey((k) => k + 1)}
        />
      )}

      <div className="ma-header-band">
        <div className="ma-header">
          <div className="ma-header__row">
            <h1 className="ma-title">{labels.title}</h1>
            <div className="ma-header__buttons">
              <button type="button" className="ma-btn ma-btn--ghost" onClick={() => comingSoon('Embed link')}>Embed link</button>
              <button type="button" className="ma-btn ma-btn--ghost" onClick={() => comingSoon('Settings')}>Settings</button>
            </div>
          </div>
        </div>

        <LMLineTab defaultTab={1} tabName={labels.tabName} />
      </div>

      <div className="ma-toolbar-wrap">
        {selectedIds.size > 0 ? (
          <BulkActionBar
            count={selectedIds.size}
            onResend={() => comingSoon('Resend invite')}
            onRemind={() => comingSoon('Remind')}
            onMove={() => comingSoon('Move to walkthrough')}
            onRevoke={handleBulkRevoke}
            onClear={clearSelection}
          />
        ) : (
          <Toolbar
            search={search}
            onSearchChange={setSearch}
            groupByLabel={labels.groupByLabel}
            statusFilter={statusFilter}
            onStatusChange={(v) => { setStatusFilter(v); setPage(1); }}
            sort={sort}
            onSortChange={(v) => { setSort(v); setPage(1); }}
            onExportCsv={() => comingSoon('Export CSV')}
            onImportCsv={() => setImportOpen(true)}
            onInviteUsers={() => openInvite()}
          />
        )}
      </div>

      {isLoading && (
        <div className="ma-skeleton">
          {[0, 1, 2].map((i) => <div key={i} className="ma-skeleton__card" />)}
        </div>
      )}

      {showEmpty && (
        <EmptyState
          materialType={materialType}
          noMaterials={!isFiltering}
          onInviteUsers={() => openInvite()}
        />
      )}

      {!isLoading && records.length > 0 && (
        <div className="ma-list">
          {records.map((material) => (
            <GroupedMaterialCard
              key={material.id}
              material={material}
              unitUpper={labels.unitUpper}
              stepPrefix={labels.stepPrefix}
              expanded={expanded.has(material.id)}
              onToggleExpand={toggleExpand}
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAllInCard={toggleAllInCard}
              onInviteToThis={(material) => openInvite(material.id)}
              onRevokeRow={handleRevokeRow}
              onCardKebab={() => comingSoon('Card actions')}
            />
          ))}
        </div>
      )}

      {!isLoading && records.length > 0 && (
        <div className="ma-footer">
          <span className="ma-footer__label">{resultLabel}</span>
          <div className="ma-pagination">
            <button
              type="button"
              className="ma-pagination__nav"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              aria-label="Previous page"
            >‹</button>
            {(pagination.range || []).map((item, idx) => (
              item === 'gap' ? (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`gap-${idx}`} className="ma-pagination__gap">…</span>
              ) : (
                <button
                  type="button"
                  key={item}
                  className={`ma-pagination__page${Number(item) === page ? ' ma-pagination__page--active' : ''}`}
                  onClick={() => goToPage(Number(item))}
                >{item}</button>
              )
            ))}
            <button
              type="button"
              className="ma-pagination__nav"
              disabled={page >= (pagination.total_pages || 1)}
              onClick={() => goToPage(page + 1)}
              aria-label="Next page"
            >›</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAccessPage;
